// Owner: mail (wave 2, plan §9.7); registry conversion by ui-layers. All e-mail links (record cells,
// headers, AI fields, mobile) open the same composer layer: `composeEmail(request)` or a click on a
// `mailto:` link inside the app. A link clicked on a record page links the message to that record.
// The composer (and its rich-text editor) loads on first open.
import { lazy, useEffect } from 'react';
import { defineGlobalLayer, openLayer } from '../shell/overlays';

const RECORD_PATH =
  /^\/o\/([a-z][a-z0-9_]*)\/r\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/** The record page the member is on, if any. */
function recordOnScreen(): { objectKey: string; recordId: string } | null {
  const m = RECORD_PATH.exec(window.location.pathname);
  return m ? { objectKey: m[1]!, recordId: m[2]! } : null;
}

/**
 * Intercepts left clicks on in-app `mailto:` links (a click listener, no key handling). It listens in the
 * capture phase: field values render their links with `stopPropagation` (so a click does not start
 * editing the cell), which would hide the click from a bubbling listener.
 */
function MailtoLinks() {
  useEffect(() => {
    const intercept = (event: MouseEvent) => {
      if (!(event.target instanceof Element) || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href^="mailto:"]');
      if (!link || !link.closest('.app')) return;
      event.preventDefault();
      // A link in a grid cell that is not selected yet: the click only selects the cell.
      if (link.closest('[data-link-guard="1"]')) return;
      const href = link.getAttribute('href')!.slice(7);
      const [addresses, query] = href.split('?');
      const params = new URLSearchParams(query);
      const recordRef = recordOnScreen();
      try {
        openLayer('composer', {
          to: decodeURIComponent(addresses ?? '')
            .split(/[,;]/)
            .map((email) => email.trim())
            .filter(Boolean)
            .map((email) => ({ email })),
          subject: params.get('subject') ?? '',
          body: params.get('body') ?? '',
          recordRef,
        });
      } catch {
        openLayer('composer', { recordRef });
      }
    };
    document.addEventListener('click', intercept, true);
    return () => document.removeEventListener('click', intercept, true);
  }, []);
  return null;
}

export default defineGlobalLayer({
  id: 'composer',
  component: lazy(() => import('./communications.layer')),
  Effect: MailtoLinks,
});
